/**
 * Layout compacto para impressão do lead — menos folhas, só dados preenchidos
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClientesService } from '@/features/clientes/services/clientes.service';
import { useClientJourney } from '@/features/vendas/hooks/useClientJourney';
import { formatDate } from '@/utils/format';
import type { Cliente } from '@/features/clientes/types/cliente';
import type { Agendamento } from '@/features/agendamentos/types/agendamento';
import {
  buildAgendamentoPrintFields,
  buildAnexosPrintFields,
  buildClientePrintFields,
  buildContratoPrintFields,
  formatAgendamentoStatus,
  type PrintField,
} from '../utils/leadPrintFormat';

interface PipelineLeadPrintViewProps {
  enabled: boolean;
  displayName: string;
  stageName?: string;
  responsavelNome: string;
  tags: string[];
  clienteId?: string;
  cliente?: Cliente | null;
  agendamento?: Agendamento | null;
}

function PrintSection({
  title,
  children,
  compact,
}: {
  title: string;
  children: ReactNode;
  compact?: boolean;
}) {
  return (
    <section className={`pipeline-lead-print-section${compact ? ' pipeline-lead-print-section--compact' : ''}`}>
      <h2 className="pipeline-lead-print-section-title">{title}</h2>
      {children}
    </section>
  );
}

function PrintFieldGrid({
  fields,
  wideLabels,
}: {
  fields: PrintField[];
  wideLabels?: string[];
}) {
  if (fields.length === 0) {
    return <p className="pipeline-lead-print-empty">—</p>;
  }

  return (
    <div className="pipeline-lead-print-grid">
      {fields.map((field) => (
        <div
          key={field.label}
          className={`pipeline-lead-print-cell${
            wideLabels?.includes(field.label) ? ' pipeline-lead-print-cell--full' : ''
          }`}
        >
          <span className="pipeline-lead-print-label">{field.label}</span>
          <span className="pipeline-lead-print-value">{field.value}</span>
        </div>
      ))}
    </div>
  );
}

export function PipelineLeadPrintView({
  enabled,
  displayName,
  stageName,
  responsavelNome,
  tags,
  clienteId,
  cliente,
  agendamento,
}: PipelineLeadPrintViewProps) {
  const { journey } = useClientJourney(clienteId || '');

  const { data: documentos = [] } = useQuery({
    queryKey: ['documentos', clienteId, 'print'],
    queryFn: () => ClientesService.listarDocumentos(clienteId!),
    enabled: enabled && !!clienteId,
    staleTime: 0,
  });

  const agendamentos =
    journey?.events.filter((e) => e.type === 'AGENDAMENTO') ?? [];
  const contratos = journey?.events.filter((e) => e.type === 'CONTRATO') ?? [];

  const metaParts = [
    stageName && `Etapa: ${stageName}`,
    `Resp.: ${responsavelNome}`,
    tags.length > 0 && tags.map((t) => `#${t}`).join(' '),
    formatDate(new Date(), 'dd/MM/yyyy HH:mm'),
  ].filter(Boolean);

  const agendamentoFields = agendamento
    ? buildAgendamentoPrintFields(agendamento)
    : null;

  const clienteFields = cliente ? buildClientePrintFields(cliente) : [];
  const wideClienteLabels = ['Endereço', 'Observações'];
  const anexosFields = buildAnexosPrintFields(documentos);

  return (
    <div className="pipeline-lead-print-only">
      <header className="pipeline-lead-print-header">
        <h1 className="pipeline-lead-print-title">{displayName}</h1>
        {metaParts.length > 0 && (
          <p className="pipeline-lead-print-meta">{metaParts.join(' · ')}</p>
        )}
      </header>

      {cliente ? (
        <PrintSection title="Cliente">
          <PrintFieldGrid fields={clienteFields} wideLabels={wideClienteLabels} />
        </PrintSection>
      ) : (
        <PrintSection title="Cliente" compact>
          <p className="pipeline-lead-print-empty">Cliente não vinculado.</p>
        </PrintSection>
      )}

      {(agendamentos.length > 0 || agendamentoFields) && (
        <PrintSection title="Agendamentos" compact>
          {agendamentos.length > 0 ? (
            <table className="pipeline-lead-print-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Hora</th>
                  <th>Status</th>
                  <th>Observações</th>
                </tr>
              </thead>
              <tbody>
                {agendamentos.map((event) => {
                  const ag = event.data;
                  return (
                    <tr key={event.id}>
                      <td>{formatDate(ag.data_agendamento) || '—'}</td>
                      <td>{ag.hora_agendamento?.slice(0, 5) || '—'}</td>
                      <td>{formatAgendamentoStatus(ag.status)}</td>
                      <td>{ag.observacoes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : agendamentoFields ? (
            <PrintFieldGrid fields={agendamentoFields} wideLabels={['Observações']} />
          ) : null}
        </PrintSection>
      )}

      {contratos.length > 0 && (
        <PrintSection title="Contratos" compact>
          {contratos.map((event) => {
            const c = event.data;
            const fields = buildContratoPrintFields(c, event.description || '—');
            return (
              <div key={event.id} className="pipeline-lead-print-contract">
                <p className="pipeline-lead-print-contract-title">
                  #{c.contrato_numero || c.id}
                </p>
                <PrintFieldGrid fields={fields} />
              </div>
            );
          })}
        </PrintSection>
      )}

      {anexosFields.length > 0 && (
        <PrintSection title="Anexos" compact>
          <table className="pipeline-lead-print-table">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Enviado em</th>
              </tr>
            </thead>
            <tbody>
              {anexosFields.map((field) => (
                <tr key={field.label}>
                  <td>{field.label}</td>
                  <td>{field.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrintSection>
      )}
    </div>
  );
}

export default PipelineLeadPrintView;
